import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../configureStore";

const selectSharedDataReducer = (state: RootState) => state.SharedDataReducer;
const selectDataTableReducer = (state: RootState) => state.DataTableReducer;
const selectDataFiltersReducer = (state: RootState) => state.DataFiltersReducer;
const selectDataExportReducer = (state: RootState) => state.DataExportReducer;
const selectDataViewsReducer = (state: RootState) => state.DataViewsReducer;
const selectLayoutReducer = (state: RootState) => state.LayoutReducer;
const selectDashboardReducer = (state: RootState) => state.DashboardReducer;
const selectDashboardFiltersReducer = (state: RootState) =>
  state.DashboardFiltersReducer;
const selectAuthenticationToken = (state: RootState) =>
  state.Authentication.user?.token as string;
const selectLocale = (state: RootState) => state.LayoutReducer.locale;

// =============================================================================
// UNIFIED SELECTORS
// =============================================================================

export const selectDataTableUnifiedState = createSelector(
  [
    selectSharedDataReducer,
    selectDataTableReducer,
    selectDataFiltersReducer,
    selectLayoutReducer,
    selectDataViewsReducer,
  ],
  (sharedData, dataTable, filters, layout, dataViews) => {
    return {
      showFilterForm: dataTable.showFilterForm,
      locale: layout.locale,
      sortModel: dataTable.sortModel,
      // Shared data
      actions: sharedData.actions,
      loading: sharedData.loading.actions || dataTable.loading,
      error: sharedData.error.actions || dataTable.error,

      // DataTable data
      columns: dataTable.columns,
      columnsVisibility: dataTable.columnsVisibility,
      pinnedColumns: dataTable.pinnedColumns,
      columnOrder: dataTable.columnOrder,
      cerealInformation: dataTable.cerealInformation,
      tableOptions: sharedData.DataTableOptions,
      filtersSideBarVisible: dataTable.filtersSideBarVisible,
      isOpenFilters: dataTable.isOpenFilters,
      filterModel: dataTable.filterModel,

      // DataViews data
      dataViews: {
        appliedView: dataViews.appliedView,
        appliedViewName: dataViews.appliedViewName,
      },

      //Filters data
      filters: {
        preselectedField: dataTable.preselectedFilterField,
        dataDateActivity: filters.dataDateActivity,
        filtersSpecies: filters.filtersSpecies,
        filtersLocation: filters.filtersLocation,
        filterUsers: filters.filterUsers,
        actionsFilters: filters.actions,
        loadingFilters: filters.loadingFilters,
        filtersSelectedLength: filters.filtersSelected,
        dataSBFilterValues: filters.dataSBFilterValues,
        locations: filters.locations,
        users: filters.users,
        selectDateRangePickerOpen: filters.dateRangePickerOpen,
        selectDataDateActivity: filters.dataDateActivity,
      },
      // Utilities
      hasData: sharedData.actions.length > 0,
      totalRecords: sharedData.actions.length,
    };
  }
);

export const selectExportDataState = createSelector(
  [selectDataExportReducer, selectDataTableReducer, selectDataFiltersReducer],
  (dataExport, dataTable, dataFilters) => {
    return {
      loadingExport: dataExport.loadingExport,
      email: dataExport.email,
      successExport: dataExport.successExport,
      columns: dataTable.columns,
      columnsVisibility: dataTable.columnsVisibility,
      dataDateActivity: dataFilters.dataDateActivity,
      filterActions: dataFilters.filterActions,
      filtersSpecies: dataFilters.filtersSpecies,
      filterUsers: dataFilters.filterUsers,
      filtersLocation: dataFilters.filtersLocation,
    };
  }
);

export const dataTableViewsConfigurationState = createSelector(
  [
    selectDataTableReducer,
    selectDataFiltersReducer,
    selectDataViewsReducer,
    selectSharedDataReducer,
  ],
  (dataTable, dataFilters, dataViews, sharedData) => {
    return {
      dataView: {
        dataViews: dataViews.dataViews,
        isCreatingView: dataViews.isCreatingView,
        saveViewSuccess: dataViews.saveViewSuccess,
        saveViewError: dataViews.saveViewError,
        reduxSavedViews: dataViews.savedViews,
        deleteViewStatus: dataViews.deleteView,
      },

      tableConfig: {
        columnsVisibility: dataTable.columnsVisibility,
        pinnedColumns: dataTable.pinnedColumns,
        columnOrder: dataTable.columnOrder,
        tableOptions: sharedData.DataTableOptions,
        sortModel: dataTable.sortModel,
      },
      filtersConfig: {
        filterNewModel: dataTable.filterModel,
        filtersSelected: dataFilters.filtersSelected,
        locations: dataFilters.locations,
        actions: dataFilters.actions,
        users: dataFilters.users,
        filtersSpecies: dataFilters.filtersSpecies,
        filtersLocation: dataFilters.filtersLocation,
        filterActions: dataFilters.filterActions,
        filterUsers: dataFilters.filterUsers,
        filterDate1: dataFilters.filterDate1,
        filterDate2: dataFilters.filterDate2,
      },
      dataDateActivityConfig: {
        dataDateActivity: dataFilters.dataDateActivity,
      },
    };
  }
);

/**
 * Dashboard Charts State Selector - Memoized
 */

export const DashboardSelectors = createSelector(
  [
    selectDashboardReducer,
    selectDashboardFiltersReducer,
    selectAuthenticationToken,
    selectLocale,
  ],
  (dashboard, dashboardFilters, token, locale) => {
    const {
      allLocations,
      loadingDashboardInfo,
      locationsSelected,
      countOfUsers,
      pocketlabVersion,
      comesFromMap,
      cropsSelected,
      selectedCrop,
      loading,
      dynamicCharts,
      activityChart,
      allDataResults,
      hasDataInCharts,
      rawRowsPocketLabTable,
      usersTable,
    } = dashboard;

    const {
      dataDateActivity,
      dateRangePickerOpen,
      dashboardFilterValues,
      locations,
    } = dashboardFilters;

    return {
      usersTable,
      token,
      locale,
      allLocations,
      loadingDashboardInfo,
      locationsSelected,
      countOfUsers,
      pocketlabVersion,
      comesFromMap,
      cropsSelected,
      selectedCrop,
      loading,
      dynamicCharts,
      activityChart,
      allDataResults,
      hasDataInCharts,
      dataDateActivity,
      dateRangePickerOpen,
      dashboardFilterValues,
      locations,
      rawRowsPocketLabTable,
    };
  }
);
